import React, { FC, PropsWithChildren } from "react"
import { useParams } from "react-router-dom"
import { Button, Grid } from "@mui/material"
import {
  HasSidebarLayout,
  DataLoad,
  EntityHeader,
  TabGroupFilter,
  InnerLink,
  ContractorInfo,
  UsersOfContractor,
  ContractorProjects,
  ContractorSensors,
  ContractorBranches,
  ContractorReports,
  ContractorTasks,
  StatefulDialog,
  ConfirmContractorDeactivationDialog,
  ContractorEventList,
  PropsInjector,
  ContractorDocumentList,
  ContractorAlertList,
  Status,
  ContractorDataLoggerList,
  ContractorGateways,
  ContractorLoRaWanSensors,
} from "src/components"
import { useContractorRelatedEntitiesAmountRequest, useContractorRequest } from "src/hooks/api"
import { Contractor, ContractorRelatedEntitiesAmountResponse } from "src/api"
import { DialogNames, SearchParamsKeys } from "src/enums"
import { useDeactivateContractor } from "src/hooks/api/useDeactivateContractor"
import { useAddPopupMessage, useDialog } from "src/hooks/ui"
import { ContractorWagnerProbeList } from "src/components/sections/contractor/contractorWagnerProbeList"
import { useActivateContractor } from "../hooks/api/useActivateContractor"

interface ContractorShowContentProps {
  contractorId?: number;
  contractor?: Contractor;
  activate?: () => void;
  deactivate?: () => void;
  isDeactivationInProgress?: boolean;
  isActivationInProgress?: boolean;
  amountRelatedEntities?: ContractorRelatedEntitiesAmountResponse;
}

const getTabs = (
  contractorId: number,
  contractor: Contractor,
  amountRelatedEntities?: ContractorRelatedEntitiesAmountResponse,
): Status[] => ([
  {
    status: "Users",
    extraLabel: String(amountRelatedEntities?.users || 0),
    content: (
      <UsersOfContractor contractorId={contractorId} />
    ),
  },
  {
    status: "Projects",
    extraLabel: String(amountRelatedEntities?.projects || 0),
    content: <ContractorProjects contractorId={contractorId} />,
  },
  {
    status: "Sensors",
    extraLabel: String(amountRelatedEntities?.sensors || 0),
    content: <ContractorSensors contractorId={contractorId} />,
  },
  {
    status: "Wagner Probes",
    extraLabel: String(amountRelatedEntities?.rapidRhProbes || 0),
    content: <ContractorWagnerProbeList contractorId={contractorId} />,
  },
  {
    status: "Data PUCKs",
    extraLabel: String(amountRelatedEntities?.dataLoggers || 0),
    content: <ContractorDataLoggerList contractorId={contractorId} />,
  },
  {
    status: "Gateways",
    extraLabel: String(amountRelatedEntities?.gateways || 0),
    content: <ContractorGateways contractorId={contractorId} />,
  },
  {
    status: "LoRaWan Sensors",
    extraLabel: String(amountRelatedEntities?.lorawanSensors || 0),
    content: <ContractorLoRaWanSensors contractorId={contractorId} />,
  },
  ...(contractor.isRoot === true ? [
    {
      status: "Branches",
      extraLabel: String(amountRelatedEntities?.branches || 0),
      content: <ContractorBranches branches={contractor.branches || []} />
    }
  ] : []),
  {
    status: "Reports",
    extraLabel: String(amountRelatedEntities?.reports || 0),
    content: <ContractorReports contractorId={contractorId} />
  },
  {
    status: "Tasks",
    extraLabel: String(amountRelatedEntities?.tasks || 0),
    content: <ContractorTasks contractorId={contractorId} />
  },
  {
    status: "Events",
    extraLabel: String(amountRelatedEntities?.events || 0),
    content: <ContractorEventList contractorId={contractorId} />,
  },
  {
    status: "Documents",
    extraLabel: String(amountRelatedEntities?.documents || 0),
    content: <ContractorDocumentList contractorId={contractorId} />,
  },
  {
    status: "Alerts",
    extraLabel: String(amountRelatedEntities?.alerts || 0),
    content: <ContractorAlertList contractorId={contractorId} />,
  },
])

const ContractorShowContent: FC<ContractorShowContentProps> = ({
  contractorId,
  contractor,
  activate,
  deactivate,
  isActivationInProgress,
  isDeactivationInProgress,
  amountRelatedEntities,
}) => {
  if (!contractor || !activate || !deactivate || !contractorId) {
    return null
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <EntityHeader
          title={contractor.name}
          actionBlockOnTheRight={<InnerLink
            to={`/contractors/${contractorId}/edit`}
            style={{ textDecoration: "none" }}
          >
            <Button variant="contained">Edit Info</Button>
          </InnerLink>}
        />
      </Grid>
      <Grid item container xs={12}>
        <ContractorInfo
          contractor={contractor}
          isActivationInProgress={isActivationInProgress as boolean}
          isDeactivationInProgress={isDeactivationInProgress as boolean}
          activate={activate}
          deactivate={deactivate}
        />
      </Grid>
      <Grid item pt={4} xs={12}>
        <TabGroupFilter
          name={SearchParamsKeys.Tab}
          statusList={getTabs(contractorId, contractor, amountRelatedEntities)}
          paramsToRemove={[
            SearchParamsKeys.Page,
            SearchParamsKeys.RowsPerPage,
            SearchParamsKeys.Contractor,
          ]}
        />
      </Grid>
    </Grid>
  )
}

const ContractorDataLoader: FC<PropsWithChildren<{ contractorId: number }>> = ({
  children,
  contractorId,
}) => {
  const addPopupMessage = useAddPopupMessage()
  const { openDialog, closeDialog } = useDialog(DialogNames.ConfirmContractorDeactivation)
  const contractorRequest = useContractorRequest({ id: contractorId })
  document.title = `CLīMIT Admin Panel - Contractor ${contractorRequest.data?.name || ''}`

  const isLoading = contractorRequest.isInitialLoading

  const isError = contractorRequest.isError

  const { mutate: activate, isLoading: isActivationInProgress } = useActivateContractor({
    options: {
      onSuccess: () => {
        contractorRequest.refetch()
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: "error" })
      },
    },
  })

  const { mutate: deactivate, isLoading: isDeactivationInProgress } = useDeactivateContractor({
    options: {
      onSuccess: () => {
        contractorRequest.refetch()
        closeDialog()
      },
      onError: ({ message: text }) => {
        addPopupMessage({ text, type: "error" })
      },
    },
  })

  const { data: amountRelatedEntities } = useContractorRelatedEntitiesAmountRequest({
    id: contractorId,
  })

  const props: ContractorShowContentProps = {
    contractorId,
    contractor: contractorRequest.data as Contractor,
    activate: () => activate({ id: contractorId }),
    deactivate: () => openDialog(),
    isActivationInProgress: isActivationInProgress,
    isDeactivationInProgress: isDeactivationInProgress,
    amountRelatedEntities,
  }

  return (
    <DataLoad isLoading={isLoading} isError={isError}>
      <PropsInjector props={props}>
        {children}
        <StatefulDialog
          name={DialogNames.ConfirmContractorDeactivation}
          wrapContent={false}
        >
          <ConfirmContractorDeactivationDialog
            contractor={contractorRequest.data?.name || ''}
            onSubmit={() => deactivate({ id: contractorId })}
            isLoading={isDeactivationInProgress}
          />
        </StatefulDialog>
      </PropsInjector>
    </DataLoad>
  )
}

const ContractorShow: FC = () => {
  const params = useParams()
  const { id } = params as { id: string }

  return (
    <HasSidebarLayout>
      <ContractorDataLoader contractorId={parseInt(id, 10)}>
        <ContractorShowContent />
      </ContractorDataLoader>
    </HasSidebarLayout>
  )
}

export default ContractorShow
